import type { ReactElement } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Card, CardContent, CardDescription, CardFooter, CardTitle } from '@/components/ui/card';
import { themeColorLinkClassNames } from '@/components/ui/utils';
import { cn } from '@/lib/utils';
import 'material-symbols';

export interface HubMediaCardProps {
    title: string;
    action: ReactElement;
    description?: string | null;
    imageUrl?: string;
}

export const HubMediaCard = ({ title, action, description, imageUrl }: HubMediaCardProps) => (
    <Card
        withBorder
        noPadding
        shadow="sm"
        cursorPointer
        className={cn(
            'group relative h-[344px] overflow-hidden transition-colors sm:h-[380px]',
            'light:hover:bg-surface-dark dark:border-transparent dark:bg-gz-blue-900 dark:hover:bg-gz-blue-550',
            // The action covers the card, so the ring belongs around the card rather than around
            // the one word that is the control's own box. Scoped to the footer and to no
            // particular element, so a second control elsewhere cannot claim it and an anchor
            // action earns it the same as a button.
            'has-[[data-slot=card-footer]_:focus-visible]:outline-2 has-[[data-slot=card-footer]_:focus-visible]:outline-offset-2',
            'has-[[data-slot=card-footer]_:focus-visible]:outline-solid has-[[data-slot=card-footer]_:focus-visible]:outline-focus-ring',
        )}
    >
        <div className="min-h-0 flex-1 p-4 pb-0">
            <div className="relative h-full w-full overflow-hidden rounded-xl bg-muted">
                {imageUrl && <img src={imageUrl} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />}
            </div>
        </div>
        <CardContent className="flex flex-none flex-col gap-2 px-4 pt-4 pb-2">
            <CardTitle className="line-clamp-2 type-card-title light:text-foreground dark:text-foreground" title={title}>
                {title}
            </CardTitle>
            {description && <CardDescription className="line-clamp-3 type-card-description dark:text-gz-neutral-200">{description}</CardDescription>}
        </CardContent>
        <CardFooter className="mt-auto justify-end border-0 px-4 pt-2 pb-4">
            <Slot
                className={cn(
                    'inline-flex items-center gap-1 rounded-sm type-body-default font-medium outline-none',
                    'transition-all group-hover:gap-2 after:absolute after:inset-0',
                    themeColorLinkClassNames,
                )}
            >
                {action}
            </Slot>
        </CardFooter>
    </Card>
);
